import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import DatePicker from "components/date-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { errorHandler } from "_services/errorHandler";
import { editFuelTypePageRouteName } from "../edit/edit.page";
import {
  useFuelTypeServiceV1ListFuelTypesQuery,
  useFuelTypeServiceV1ActivateFuelTypesMutation,
  useFuelTypeServiceV1DeactivateFuelTypesMutation,
} from "_services/modules/fuel_type";

const sortFieldMap: Record<string, string> = {
  fuelTypeName: "fuel_type_name",
  priceFuel: "price_fuel",
  effectiveDate: "effective_date",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface FuelTypeRow {
  id: string;
  fuelTypeName: string;
  priceFuel: number;
  effectiveDate: string;
  description: string;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const formatPrice = (value: number) =>
  value.toLocaleString("id-ID");

const useFuelTypeList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("fuelType");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<FuelTypeRow | null>(null);
  const historyPanel = useHistoryPanel("fuel_type");

  const [activateFuelTypes] = useFuelTypeServiceV1ActivateFuelTypesMutation();
  const [deactivateFuelTypes] = useFuelTypeServiceV1DeactivateFuelTypesMutation();

  const { data: apiResponse, isLoading, refetch } = useFuelTypeServiceV1ListFuelTypesQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    fuelTypeName: filterValues.fuelTypeName || undefined,
    priceFuel: filterValues.priceFuel ? Number(filterValues.priceFuel) : undefined,
    effectiveDate: filterValues.effectiveDate || undefined,
    description: filterValues.description || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });

  const tableData: FuelTypeRow[] = useMemo(() => {
    if (!apiResponse?.fuel_types) return [];
    return apiResponse.fuel_types.map((item) => ({
      id: item.id ?? "",
      fuelTypeName: item.fuel_type_name ?? "",
      priceFuel: item.price_fuel ?? 0,
      effectiveDate: item.effective_date ?? "",
      description: item.description ?? "",
      status: (item.status as "active" | "inactive") ?? "inactive",
      createdBy: item.created_by ?? "",
      createdAt: item.created_at ?? "",
      updatedBy: item.updated_by ?? "",
      updatedAt: item.updated_at ?? "",
    }));
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map(String);
      if (bulkAction === "activate") {
        await activateFuelTypes({ fuelTypeV1FuelTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else {
        await deactivateFuelTypes({ fuelTypeV1FuelTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: FuelTypeRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    try {
      if (!toggleRow) return;
      if (toggleRow.status === "active") {
        await deactivateFuelTypes({ fuelTypeV1FuelTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateFuelTypes({ fuelTypeV1FuelTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle status failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: FuelTypeRow) => {
    navigate(editFuelTypePageRouteName(row.id.toString()));
  };

  const columns: Columns<FuelTypeRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "fuelTypeName",
        fieldId: "fuelTypeName",
        label: t("label-fuel-type-name"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "priceFuel",
        fieldId: "priceFuel",
        label: t("label-price-fuel"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-price-fuel-${row.id}`}>{formatPrice(row.priceFuel)}</span>
        ),
      },
      {
        id: "effectiveDate",
        fieldId: "effectiveDate",
        label: t("label-effective-date"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => (
          <DatePicker
            value={value}
            onChange={(d) => onChange(d)}
            reserveHelperSpace={false}
          />
        ),
      },
      {
        id: "description",
        fieldId: "description",
        label: t("label-description"),
        sortable: false,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm text-greyScale-40 truncate max-w-[200px] inline-block" data-testid={`col-description-${row.id}`}>
            {row.description || "-"}
          </span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-fuel"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-fuel" : "btn-activate-fuel",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.fuelTypeName),
                  testId: "btn-track-history-fuel",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return {
    data: tableData,
    isLoading,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    historyPanel,
    t,
  };
};

export default useFuelTypeList;
